import { useMemo } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { LeadStatus } from '@conversia/shared-types';
import * as conversationsApi from '@/api/conversations.api';
import * as channelsApi from '@/api/channels.api';
import { lugaresParaVender } from '@/api/supervision.api';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConversationItem } from './ConversationItem';
import { HelpTip } from '@/components/ui/HelpTip';
import { Superfiltro } from '@/components/datos/Superfiltro';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { channelTypeLabel } from '@/lib/badges';
import { sinFiltros, type Faceta } from '@/lib/superfiltro';

// La BANDEJA: «Atención» (alta A1 de la auditoría: las conversaciones en WAITING_HUMAN, a
// quién le debo respuesta YA) y la TEMPERATURA del lead (embudo). Antes eran píldoras sueltas;
// desde el superfiltro (Josué, 14-oct) son una faceta y viven en la URL (?bandeja=CALIENTE).
const ATENCION = 'ATENCION';
const BANDEJAS: Array<{ valor: LeadStatus | typeof ATENCION; etiqueta: string }> = [
  { valor: ATENCION, etiqueta: 'Atención' },
  { valor: 'CALIENTE', etiqueta: 'Caliente' },
  { valor: 'TIBIO', etiqueta: 'Tibio' },
  { valor: 'FRIO', etiqueta: 'Frío' },
  { valor: 'CERRADO', etiqueta: 'Cerrado' },
  { valor: 'NO_CERRADO', etiqueta: 'No cerrado' },
];

const TIPOS = [
  { valor: 'individual', etiqueta: 'Individuales' },
  { valor: 'group', etiqueta: 'Grupos' },
] as const;

/** Las claves de la URL de la bandeja. Un valor inventado cae al de por defecto (trampa 3). */
const FILTROS_DE_LA_BANDEJA = { bandeja: '', tipo: '', sucursal: '', canal: '', buscar: '' };
const VALIDOS_DE_LA_BANDEJA = {
  bandeja: BANDEJAS.map((b) => b.valor),
  tipo: TIPOS.map((t) => t.valor),
};

/** Cuántos chats trae cada página. El servidor no da más de 200 por pedido. */
const POR_PAGINA = 100;

export function ConversationList({
  selectedId,
  onSelect,
}: {
  selectedId?: string;
  onSelect: (id: string) => void;
}) {
  const { filtros, poner, ponerVarios } = useFiltrosEnUrl(FILTROS_DE_LA_BANDEJA, VALIDOS_DE_LA_BANDEJA);
  const filters = {
    status: filtros.bandeja === ATENCION ? ('WAITING_HUMAN' as const) : undefined,
    leadStatus: filtros.bandeja && filtros.bandeja !== ATENCION ? (filtros.bandeja as LeadStatus) : undefined,
    channelId: filtros.canal || undefined,
    ubicacionId: filtros.sucursal || undefined,
    search: filtros.buscar,
    type: (filtros.tipo || undefined) as 'individual' | 'group' | undefined,
  };

  const { data: channels } = useQuery({
    queryKey: ['channels', 'list'],
    queryFn: () => channelsApi.listChannels(),
  });
  // Las sucursales que atienden algún canal: el filtro solo sale si hay más de una.
  const { data: lugares } = useQuery({
    queryKey: ['pasar-venta', 'lugares'],
    queryFn: lugaresParaVender,
    retry: false,
  });
  const sucursales = (lugares ?? []).filter((l) => channels?.some((c) => c.ubicacionId === l.id));

  const facetas: Faceta[] = [
    { clave: 'bandeja', titulo: 'Bandeja', todas: 'Todas', opciones: BANDEJAS.map((b) => ({ valor: b.valor, etiqueta: b.etiqueta })) },
    { clave: 'tipo', titulo: 'Tipo', todas: 'Todas', opciones: TIPOS.map((t) => ({ ...t })) },
    // Sucursal: los chats de los canales de esa tienda (solo si hay más de una).
    {
      clave: 'sucursal',
      titulo: 'Sucursal',
      todas: 'Todas',
      opciones: sucursales.length > 1 ? sucursales.map((s) => ({ valor: s.id, etiqueta: s.nombre })) : [],
    },
    {
      clave: 'canal',
      titulo: 'Canal',
      opciones: (channels ?? []).map((c) => ({ valor: c.id, etiqueta: `${c.name} · ${channelTypeLabel[c.type]}` })),
    },
  ];

  // DE A PÁGINAS, Y NO "LOS 100 PRIMEROS".
  //
  // La lista pedía 100 y ahí se quedaba, sin avisar. Esteban tenía 214 chats el
  // 22-sep-2026: 114 no aparecían en ningún lado, y lo que él pidió era
  // textual *"que yo pueda ver todos los chats realmente"*. Ahora se cargan de
  // a 100, se dice cuántos hay en total y se ofrece cargar más.
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['conversations', 'list', filters],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      conversationsApi.listConversations({
        status: filters.status,
        leadStatus: filters.leadStatus,
        channelId: filters.channelId,
        ubicacionId: filters.ubicacionId,
        search: filters.search || undefined,
        type: filters.type,
        skip: pageParam,
        take: POR_PAGINA,
      }),
    getNextPageParam: (ultima, todas) => {
      const cargados = todas.reduce((n, p) => n + p.items.length, 0);
      return cargados < ultima.total ? cargados : undefined;
    },
  });
  const total = data?.pages[0]?.total ?? 0;

  // Las conversaciones en "Atención" se muestran primero para que el
  // operador las atienda sin buscarlas. `sort` es estable, así que el resto
  // conserva el orden que devuelve la API (por última actividad).
  const items = useMemo(() => {
    const list = data?.pages.flatMap((p) => p.items) ?? [];
    return [...list].sort((a, b) => {
      const aw = a.status === 'WAITING_HUMAN' ? 0 : 1;
      const bw = b.status === 'WAITING_HUMAN' ? 0 : 1;
      return aw - bw;
    });
  }, [data]);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden border-r border-surface-border bg-surface sm:w-88">
      <div className="border-b border-surface-border p-3">
        <Superfiltro
          compacta
          buscar={filtros.buscar}
          onBuscar={(v) => poner('buscar', v)}
          placeholder="Buscar contacto…"
          facetas={facetas}
          valores={filtros}
          onCambiar={ponerVarios}
          onLimpiar={() => ponerVarios(sinFiltros(facetas))}
          resultados={isLoading ? undefined : `${total} ${total === 1 ? 'chat' : 'chats'}`}
          acciones={<HelpTip helpKey="conversations" />}
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <EmptyState title="Sin conversaciones" description="No hay conversaciones con estos filtros." className="m-4" />
        ) : (
          <ul className="space-y-0.5 p-2">
            {items.map((c) => (
              <li key={c.id}>
                <ConversationItem
                  conversation={c}
                  selected={selectedId === c.id}
                  onSelect={() => onSelect(c.id)}
                />
              </li>
            ))}
          </ul>
        )}
        {items.length > 0 && (
          <div className="space-y-2 p-3 text-center text-xs text-slate-500 dark:text-slate-400">
            <p>
              Mostrando {items.length} de {total}
            </p>
            {hasNextPage && (
              <button
                type="button"
                onClick={() => void fetchNextPage()}
                disabled={isFetchingNextPage}
                className="min-h-11 w-full rounded-lg bg-[var(--mac-btn)] text-sm text-slate-900 active:bg-[var(--mac-btn-pulsado)] disabled:opacity-50 dark:text-slate-100 lateral:min-h-7 lateral:rounded-md"
              >
                {isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
